import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { FRAU_MEETS_BOERSE_CONFIG } from '@/consts/frauMeetsBoerse';
import type { FmbSplitSection } from '@/consts/frauMeetsBoerse/types';
import FmbLayout, { FMB_SERIF as SERIF, fmbInternalPath } from '@/components/frauMeetsBoerse/FmbLayout';
import FmbImageSlot from '@/components/frauMeetsBoerse/FmbImageSlot';
import FmbCheckList from '@/components/frauMeetsBoerse/FmbCheckList';
import FmbFaq from '@/components/frauMeetsBoerse/FmbFaq';
import FmbTestimonialMarquee from '@/components/frauMeetsBoerse/FmbTestimonialMarquee';
import { useBunnyPlaybackReveal } from '@/hooks/useBunnyPlaybackReveal';

const config = FRAU_MEETS_BOERSE_CONFIG;

function Hero() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Der CTA unter dem Video erscheint erst, wenn das Webinar weit genug
  // gelaufen ist — vorher wäre es nur eine Ablenkung.
  const showCta = useBunnyPlaybackReveal(
    iframeRef,
    config.hero.delayedCta.revealAfterSeconds,
    config.heroVideoUrl !== null,
  );

  return (
    <section className="mx-auto flex max-w-[1040px] flex-col items-center gap-7 px-[clamp(20px,5vw,40px)] pt-12 pb-[72px] text-center">
      <div className="flex items-center gap-2.5 rounded-full border border-[var(--fmb-badge-border)] px-[18px] py-2 text-[13px] font-bold tracking-[0.12em] uppercase text-[var(--fmb-accent)]">
        <span className="h-2 w-2 shrink-0 rounded-full bg-[var(--fmb-accent)]" />
        {config.hero.badge}
      </div>
      <h1
        className={`${SERIF} m-0 max-w-[17ch] text-[clamp(44px,7.5vw,76px)] leading-[1.04] font-normal text-balance`}
      >
        {config.hero.titleLeading}
        <em className="text-[var(--fmb-accent)]">{config.hero.titleAccent}</em>
      </h1>
      <p className="m-0 max-w-[56ch] text-[clamp(17px,2.2vw,20px)] leading-[1.6] text-pretty text-[var(--fmb-muted)]">
        {config.hero.paragraph}
      </p>
      <div className="text-[15px] font-bold tracking-[0.02em] text-[var(--fmb-accent)]">{config.hero.videoLeadIn}</div>
      <div className="mt-4 h-[clamp(220px,47vw,495px)] w-full max-w-[880px] overflow-hidden rounded-[18px] bg-black shadow-[0_24px_60px_var(--fmb-shadow-hero)]">
        <iframe
          ref={iframeRef}
          src={config.heroVideoUrl ?? undefined}
          title={config.images.hero.alt}
          loading="lazy"
          className="h-full w-full border-0"
          allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
          allowFullScreen
        />
      </div>
      <div className="text-[13px] tracking-[0.02em] text-[var(--fmb-faint)]">{config.hero.videoTrustLine}</div>
      {showCta && (
        <div className="animate-fmb-reveal flex flex-col items-center gap-2.5">
          <Link
            to={fmbInternalPath(config.videosPath)}
            className="cursor-pointer rounded-full bg-[var(--fmb-accent)] px-10 py-[18px] text-center text-[17px] font-bold text-white no-underline shadow-[0_10px_24px_var(--fmb-shadow-cta)] transition-colors hover:bg-[var(--fmb-accent-dark)]"
          >
            {config.hero.delayedCta.label}
          </Link>
          <div className="max-w-[46ch] text-[13px] leading-[1.5] text-[var(--fmb-faint)]">
            {config.hero.delayedCta.microcopy}
          </div>
        </div>
      )}
    </section>
  );
}

function PressStrip() {
  return (
    <section className="border-y border-[var(--fmb-border)]">
      <div className="mx-auto flex max-w-[1040px] flex-wrap items-center justify-center gap-[clamp(24px,4vw,48px)] px-[clamp(20px,5vw,40px)] py-7">
        <div className="text-[12px] font-bold tracking-[0.16em] uppercase text-[var(--fmb-label)]">
          {config.pressLabel}
        </div>
        {config.images.press.map((logo) => (
          <FmbImageSlot
            key={logo.placeholderLabel}
            image={logo}
            fit="contain"
            className="h-11 w-[170px] opacity-75 grayscale"
          />
        ))}
      </div>
    </section>
  );
}

function SectionHeading({ number, heading }: { number: string; heading: string }) {
  return (
    <div className="flex items-baseline gap-5">
      <div className={`${SERIF} text-[22px] text-[var(--fmb-accent)]`}>{number}</div>
      <h2 className={`${SERIF} m-0 text-[clamp(34px,5vw,44px)] leading-[1.1] font-normal text-balance`}>{heading}</h2>
    </div>
  );
}

function Benefits() {
  return (
    <section className="mx-auto flex max-w-[1040px] flex-col gap-10 px-[clamp(20px,5vw,40px)] pt-[clamp(56px,9vw,88px)] pb-16">
      <SectionHeading number={config.benefits.sectionNumber} heading={config.benefits.heading} />
      <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-x-12">
        {config.benefits.items.map((item) => (
          <div key={item.title} className="flex flex-col gap-3.5 border-t border-[var(--fmb-ink)] py-8">
            <div className={`${SERIF} text-[19px] italic text-[var(--fmb-accent)]`}>{item.kicker}</div>
            <h3 className="m-0 text-[22px] leading-[1.3] font-bold">{item.title}</h3>
            <p className="m-0 text-[16px] leading-[1.65] text-pretty text-[var(--fmb-muted)]">{item.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Problem() {
  return (
    <section className="bg-[var(--fmb-ink)] text-[var(--fmb-bg)]">
      <div className="mx-auto grid max-w-[1040px] grid-cols-[repeat(auto-fit,minmax(340px,1fr))] items-center gap-y-12 gap-x-[clamp(40px,6vw,72px)] px-[clamp(20px,5vw,40px)] py-[clamp(56px,10vw,96px)]">
        <div className="flex flex-col gap-7">
          <div className={`${SERIF} text-[22px] text-[var(--fmb-accent-light)]`}>{config.problem.kicker}</div>
          <blockquote
            className={`${SERIF} m-0 text-[clamp(32px,4.5vw,42px)] leading-[1.2] font-normal text-balance`}
          >
            {config.problem.quoteLeading}
            <em className="text-[var(--fmb-accent-light)]">{config.problem.quoteAccent}</em>"
          </blockquote>
          <div className="text-[14px] tracking-[0.08em] uppercase text-[var(--fmb-label)]">
            {config.problem.attribution}
          </div>
          <p className="m-0 text-[17px] leading-[1.7] text-pretty text-[var(--fmb-dark-muted)]">
            {config.problem.paragraph}
            <strong className="text-[var(--fmb-bg)]">{config.problem.paragraphBold}</strong>
          </p>
        </div>
        <div className="flex flex-col">
          {config.problem.risks.map((risk, index) => (
            <div
              key={risk.label}
              className={`flex flex-col gap-1.5 py-6 ${index < config.problem.risks.length - 1 ? 'border-b border-[var(--fmb-dark-border)]' : ''}`}
            >
              <div className="text-[13px] font-bold tracking-[0.12em] uppercase text-[var(--fmb-accent-light)]">
                {risk.label}
              </div>
              <div className="text-[17px] leading-[1.55] text-[var(--fmb-dark-text)]">{risk.text}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SplitSection({ content }: { content: FmbSplitSection }) {
  return (
    <section className="mx-auto grid max-w-[1040px] grid-cols-[repeat(auto-fit,minmax(340px,1fr))] items-start gap-y-12 gap-x-[clamp(40px,6vw,72px)] px-[clamp(20px,5vw,40px)] py-[clamp(56px,10vw,96px)]">
      <div className="flex flex-col gap-6 lg:sticky lg:top-10">
        <div className={`${SERIF} text-[22px] text-[var(--fmb-accent)]`}>{content.kicker}</div>
        <h2 className={`${SERIF} m-0 text-[clamp(36px,5vw,48px)] leading-[1.08] font-normal text-balance`}>
          {content.headingLeading}
          <em className="text-[var(--fmb-accent)]">{content.headingAccent}</em>
        </h2>
        <p className="m-0 text-[17px] leading-[1.7] text-pretty text-[var(--fmb-muted)]">{content.paragraph}</p>
        <div className="flex flex-col gap-2.5">
          <Link
            to={fmbInternalPath(config.videosPath)}
            className="cursor-pointer self-start rounded-full bg-[var(--fmb-accent)] px-8 py-4 text-[16px] font-bold text-white no-underline transition-colors hover:bg-[var(--fmb-accent-dark)]"
          >
            {content.ctaLabel}
          </Link>
          <div className="text-[13px] text-[var(--fmb-faint)]">{content.ctaSubline}</div>
        </div>
      </div>
      <FmbCheckList label={content.listLabel} items={content.listItems} />
    </section>
  );
}

function Testimonials() {
  return (
    <section className="border-y border-[var(--fmb-border)] bg-[var(--fmb-section-bg)]">
      <div className="mx-auto flex max-w-[1100px] flex-col gap-12 px-[clamp(20px,5vw,40px)] py-[clamp(56px,10vw,96px)]">
        <div className="flex flex-wrap items-baseline justify-between gap-6">
          <SectionHeading number={config.testimonials.sectionNumber} heading={config.testimonials.heading} />
          <div className="text-[14px] text-[var(--fmb-faint)]">{config.testimonials.ratingNote}</div>
        </div>
        <FmbTestimonialMarquee video={config.testimonials.video} items={config.testimonials.items} />
      </div>
    </section>
  );
}

function Hosts() {
  return (
    <section className="mx-auto grid max-w-[1040px] grid-cols-[repeat(auto-fit,minmax(320px,1fr))] items-center gap-y-12 gap-x-[clamp(40px,6vw,72px)] px-[clamp(20px,5vw,40px)] py-[clamp(56px,10vw,96px)]">
      <FmbImageSlot image={config.images.team} fit="contain" className="h-[480px] w-full rounded-[18px]" />
      <div className="flex flex-col gap-6">
        <div className={`${SERIF} text-[22px] text-[var(--fmb-accent)]`}>{config.hosts.kicker}</div>
        <h2 className={`${SERIF} m-0 text-[clamp(34px,5vw,44px)] leading-[1.1] font-normal text-balance`}>
          {config.hosts.headingLeading}
          <em className="text-[var(--fmb-accent)]">{config.hosts.headingAccent}</em>
        </h2>
        <p className="m-0 text-[17px] leading-[1.7] text-pretty text-[var(--fmb-muted)]">{config.hosts.paragraph}</p>
        <div className="flex flex-wrap gap-8 border-t border-[var(--fmb-border-warm)] pt-6">
          {config.hosts.stats.map((stat) => (
            <div key={stat.label} className="flex flex-col gap-1">
              <div className={`${SERIF} text-[34px]`}>{stat.value}</div>
              <div className="text-[13px] tracking-[0.04em] text-[var(--fmb-faint)]">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="mx-auto max-w-[1100px] px-[clamp(20px,5vw,40px)] pb-[clamp(56px,10vw,96px)]">
      <div className="flex flex-col items-center gap-6 rounded-3xl bg-[var(--fmb-accent)] px-[clamp(28px,6vw,64px)] py-[clamp(48px,8vw,80px)] text-center text-white">
        <div className="text-[13px] font-bold tracking-[0.14em] uppercase text-[var(--fmb-cta-kicker)]">
          {config.ctaBanner.kicker}
        </div>
        <h2 className={`${SERIF} m-0 max-w-[20ch] text-[clamp(38px,6vw,54px)] leading-[1.08] font-normal text-balance`}>
          {config.ctaBanner.headingLeading}
          <em>{config.ctaBanner.headingAccent}</em>
        </h2>
        <p className="m-0 max-w-[52ch] text-[18px] leading-[1.6] text-pretty text-[var(--fmb-cta-text)]">
          {config.ctaBanner.paragraph}
        </p>
        <Link
          to={fmbInternalPath(config.videosPath)}
          className="mt-2 cursor-pointer rounded-full bg-white px-11 py-[18px] text-center text-[17px] font-extrabold text-[var(--fmb-accent)] no-underline transition-colors hover:bg-[var(--fmb-cta-button-hover)]"
        >
          {config.ctaBanner.ctaLabel}
        </Link>
        <div className="text-[13px] italic text-[var(--fmb-cta-ps)]">{config.ctaBanner.psLine}</div>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section className="mx-auto flex w-full max-w-[800px] flex-col gap-10 px-[clamp(20px,5vw,40px)] pb-[clamp(72px,11vw,112px)]">
      <SectionHeading number={config.faq.sectionNumber} heading={config.faq.heading} />
      <FmbFaq items={config.faq.items} />
    </section>
  );
}

export default function FrauMeetsBoersePage() {
  return (
    <FmbLayout title={config.pageTitle}>
      <Hero />
      <PressStrip />
      <Benefits />
      <Problem />
      <SplitSection content={config.solution} />
      <Testimonials />
      <SplitSection content={config.nextStep} />
      <Hosts />
      <CtaBanner />
      <FaqSection />
    </FmbLayout>
  );
}
